import React from 'react'
import {
  Button,
  Dialog,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography,
} from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import CheckIcon from 'components/material-symbol/icons/Check'
import ChatBubbleIcon from 'components/material-symbol/icons/ChatBubble'
import CloseIcon from 'components/material-symbol/icons/Close'
import EventIcon from 'components/material-symbol/icons/Event'
import VideocamIcon from 'components/material-symbol/icons/Videocam'

const ALWAYS_RECEIVED = [
  { Icon: VideocamIcon, label: 'Lesson videos and feedback' },
  { Icon: EventIcon, label: 'Scheduling and reminders' },
  { Icon: ChatBubbleIcon, label: 'Replies to your messages' },
]

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  content: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: '1.125rem',
    lineHeight: 1.4,
  },
  closeButton: {
    marginTop: -4,
    marginRight: -8,
    color: theme.palette.text.secondary,
  },
  description: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  toggleRow: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
  },
  toggleLabel: {
    fontWeight: 600,
  },
  alwaysTitle: {
    marginTop: theme.spacing(3),
    fontWeight: 600,
  },
  alwaysIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  done: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

const PromotionsSheet = ({
  open,
  onClose,
  coachName,
  enabled,
  saving,
  onChange,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const handleToggle = event => onChange(event.target.checked)

  const body = (
    <div className={classes.content}>
      <div className={classes.header}>
        <Typography component="h2" className={classes.title}>
          Promotions from {coachName}
        </Typography>
        <IconButton
          aria-label="Close"
          size="small"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <Typography variant="body2" className={classes.description}>
        {coachName} may message you about clinics, lesson packages and events.
        Promotions arrive as regular messages in this chat.
      </Typography>
      <div className={classes.toggleRow}>
        <div>
          <Typography className={classes.toggleLabel} id="promotions-label">
            Send me promotions
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Applies to {coachName} only
          </Typography>
        </div>
        <Switch
          color="primary"
          checked={enabled}
          disabled={saving}
          onChange={handleToggle}
          inputProps={{ 'aria-labelledby': 'promotions-label' }}
        />
      </div>
      <Typography variant="body2" className={classes.alwaysTitle}>
        You&apos;ll always get
      </Typography>
      <List dense disablePadding>
        {ALWAYS_RECEIVED.map(({ Icon, label }) => (
          <ListItem key={label} disableGutters>
            <ListItemIcon className={classes.alwaysIcon}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={label} />
          </ListItem>
        ))}
      </List>
      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          className={classes.done}
          startIcon={<CheckIcon />}
          onClick={onClose}
        >
          Done
        </Button>
      </div>
    </div>
  )

  if (isSmall) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        classes={{ paper: classes.drawerPaper }}
      >
        {body}
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {body}
    </Dialog>
  )
}

export default PromotionsSheet
